import { computePosition, size } from '@floating-ui/dom';
import useResizeObserver from '@react-hook/resize-observer';
import { isNull } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';

/**
 * @deprecated use useOptionContainerViewportAwareResize instead
 */
export function useOptionContainerWidthSync<E extends HTMLElement>() {
  const [width, setWidth] = useState<number | undefined>();
  const triggerRef = useRef<E>(null);

  // using triggerRef.current instead of triggerRef as dependency fixes an issue where the dropdown width would not be
  // synced when opening the "Aufgabe erstellen" route directly
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setWidth(triggerRef.current?.offsetWidth), [triggerRef.current?.offsetWidth]);

  useResizeObserver(triggerRef, ({ target }) => setWidth((target as HTMLElement).offsetWidth));

  const contentStyle = useMemo(() => ({ minWidth: width ? `${width}px` : undefined }), [width]);

  return {
    contentStyle,
    triggerRef,
  };
}

/**
 * Makes a listbox or combobox at least as wide as the trigger (button).
 * Useful when a dropdown is wider than its content, so that the options container is aligned with the button.
 */
export function useOptionContainerViewportAwareResize<DropdownTrigger extends HTMLElement, DropdownContainer extends HTMLElement>() {
  const [triggerWidth, setWidth] = useState<number | undefined>();
  const triggerRef = useRef<DropdownTrigger>(null);
  const containerRef = useRef<DropdownContainer>(null);

  const triggerElement = triggerRef.current;
  const containerElement = containerRef.current;

  useResizeObserver(triggerRef, ({ target }) => setWidth((target as HTMLElement).offsetWidth));

  useEffect(() => setWidth(triggerElement?.offsetWidth), [triggerElement?.offsetWidth]);

  useEffect(() => {
    if (isNull(triggerElement) || isNull(containerElement)) {
      return;
    }

    const setViewportAwareDropdownDimensions = () => {
      computePosition(triggerElement, containerElement, {
        middleware: [
          size({
            apply({ availableWidth, availableHeight, elements }) {
              elements.floating.style.minWidth = `${triggerWidth ?? 0}px`;
              elements.floating.style.maxWidth = `${availableWidth}px`;
              elements.floating.style.maxHeight = `${availableHeight}px`;
            },
          }),
        ],
      });
    };

    triggerElement.addEventListener('click', setViewportAwareDropdownDimensions);
    return () => triggerElement.removeEventListener('click', setViewportAwareDropdownDimensions);
  }, [triggerElement, containerElement, triggerWidth]);

  useEffect(() => {
    if (!isNull(containerElement)) {
      containerElement.style.overflow = 'auto';
      containerElement.classList.add('tds-option-container-popper');
    }
  }, [containerElement]);

  return {
    triggerRef,
    containerRef,
  };
}
